"use client";

interface CloudflareLoaderProps {
  src: string;
  width: number;
  quality?: number;
}

/**
 * Fixed width ladder for Cloudflare transforms.
 * Snapping reduces unique transformations (billed per source × params)
 * while rounding up preserves sharpness.
 */
const WIDTH_BUCKETS = [64, 128, 256, 640, 1080, 1600] as const;
const MAX_TRANSFORM_WIDTH = WIDTH_BUCKETS[WIDTH_BUCKETS.length - 1];

function snapWidth(width: number): number {
  const clamped = Math.min(Math.max(1, Math.ceil(width)), MAX_TRANSFORM_WIDTH);
  for (const bucket of WIDTH_BUCKETS) {
    if (clamped <= bucket) return bucket;
  }
  return MAX_TRANSFORM_WIDTH;
}

/**
 * Custom Next.js image loader for Cloudflare Image Resizing.
 * Transforms image URLs to use Cloudflare's cdn-cgi/image/ endpoint.
 */
export default function cloudflareLoader({ src, width, quality }: CloudflareLoaderProps) {
  // Handle Google profile images - they use format like =s96-c, we'll replace with requested width
  // This ensures the loader properly implements the width parameter as required by Next.js
  if (src.includes('lh3.googleusercontent.com')) {
    // Replace size parameter (e.g., =s96-c) with requested width
    // The pattern =s{number}-c is Google's size format
    return src.replace(/=s\d+-c/, `=s${width}-c`);
  }
  
  const cdnBase = 'https://cdn.thewheelvisualizer.com';
  const isProd = process.env.NODE_ENV === 'production';
  let finalizedSrc = src;

  // Normalize R2 public URLs to the custom CDN domain so they can be resized.
  // CRITICAL: Only do this in production. On localhost, dev images aren't on the prod CDN.
  if (isProd && (src.includes('.r2.dev') || src.includes('r2.cloudflarestorage.com'))) {
    try {
      const url = new URL(src);
      finalizedSrc = `${cdnBase}${url.pathname}`;
    } catch {
      console.error('Failed to parse image src for normalization:', src);
    }
  }

  const snappedWidth = snapWidth(width);
  
  // Custom optimization parameters
  const params = [
    `width=${snappedWidth}`,
    `quality=${quality || 75}`,
    'format=auto',
    // If transforms fail or the free monthly unique limit is exceeded, serve the original.
    'onerror=redirect',
  ];
  
  // Only use Cloudflare Image Resizing if the URL is on our custom CDN.
  // This ensures it works in prod and for prod-images viewed locally.
  if (finalizedSrc.startsWith(cdnBase)) {
    const path = finalizedSrc.replace(cdnBase, '');
    return `${cdnBase}/cdn-cgi/image/${params.join(',')}${path}`;
  }
  
  // If src is a relative path starting with /
  if (finalizedSrc.startsWith('/')) {
    // For relative paths (static assets in public/ folder), we can't optimize via CDN
    // but Next.js requires we use the width parameter. Add it as a query param.
    // The browser will ignore it for static assets, but this satisfies Next.js's requirement.
    const separator = finalizedSrc.includes('?') ? '&' : '?';
    return `${finalizedSrc}${separator}w=${width}`;
  }

  // Fallback for other URLs (localhost, placeholder services, etc.)
  // For R2 URLs in development, we can't optimize via CDN but still need to use width parameter
  // Next.js requires the loader to use the width parameter even if optimization isn't possible
  if (finalizedSrc.includes('.r2.dev') && !isProd) {
    // In development, add width as query param to acknowledge it (R2 won't use it, but satisfies Next.js)
    // This ensures Next.js knows we're implementing the width parameter
    try {
      const url = new URL(finalizedSrc);
      url.searchParams.set('w', width.toString());
      return url.toString();
    } catch {
      // If URL parsing fails, return as-is (shouldn't happen but safe fallback)
      return finalizedSrc;
    }
  }
  
  // For other non-optimizable URLs (localhost, placeholder services, etc.)
  // Next.js requires we use the width parameter. Add it as a query param.
  // The server/browser may ignore it, but this satisfies Next.js's requirement.
  try {
    const url = new URL(finalizedSrc);
    url.searchParams.set('w', width.toString());
    return url.toString();
  } catch {
    // If URL parsing fails (e.g., invalid URL), add width as query param manually
    const separator = finalizedSrc.includes('?') ? '&' : '?';
    return `${finalizedSrc}${separator}w=${width}`;
  }
}
